import { useQuery } from '@tanstack/react-query';

import { effectivePermissionsClient } from '@dynatrace-sdk/client-platform-management-service';

import { permissionsQueryClient } from '../utils/permissionsQueryClient.js';

/**
 * @public
 */
export interface PermissionDetail {
  permission: string;
  granted: 'true' | 'false' | 'condition';
}

/**
 * @public
 */
export interface PermissionsResult {
  isAllowed: boolean;
  isLoading: boolean;
  details: PermissionDetail[];
}

async function resolvePermissions(
  permissions: string[],
): Promise<PermissionDetail[]> {
  const result = await effectivePermissionsClient.resolveEffectivePermissions({
    body: {
      permissions: permissions.map((permission) => ({ permission })),
    },
  });

  return (result ?? []).map(({ permission, granted }) => ({
    permission,
    granted,
  }));
}

/**
 * Resolves whether the current user has all of the specified IAM permissions.
 * Results are cached for the lifetime of the page — permissions are never refetched automatically.
 * The `"condition"` grant state (management-zone scoped) is treated as allowed.
 *
 * @param permissions - IAM permission strings to check, e.g. `['state:app-states:write']`
 * @public
 */
export function usePermissions(permissions: string[]): PermissionsResult {
  const queryKey = [
    'permissions',
    ...permissions.slice().sort((a, b) => a.localeCompare(b)),
  ];

  const { data, isLoading, isError } = useQuery(
    {
      queryKey,
      queryFn: () => resolvePermissions(permissions),
      enabled: permissions.length > 0,
    },
    permissionsQueryClient,
  );

  const details = data ?? [];
  const isAllowed =
    !isLoading &&
    !isError &&
    (permissions.length === 0 ||
      (details.length > 0 &&
        details.every(
          ({ granted }) => granted === 'true' || granted === 'condition',
        )));

  return { isAllowed, isLoading, details };
}
